import { authApi } from "@/services/api";

interface SessionTestResult {
    timestamp: string;
    test: string;
    success: boolean;
    data?: unknown;
    error?: string;
    duration?: number;
}

class SessionPersistenceTest {
    private results: SessionTestResult[] = [];
    private isRunning = false;

    async runComprehensiveTest(): Promise<SessionTestResult[]> {
        if (this.isRunning) return this.results;

        this.isRunning = true;
        this.results = [];

        // Test 1: Check initial auth status
        await this.addTestResult("Initial Auth Status", async () => {
            return await authApi.getAuthStatus();
        });

        // Test 2: Check /me endpoint
        await this.addTestResult("Current User Check", async () => {
            return await authApi.getCurrentUser();
        });

        // Test 3: Check browser storage
        await this.addTestResult("Browser Storage Check", async () => {
            return {
                localStorage: Object.keys(localStorage),
                sessionStorage: Object.keys(sessionStorage),
                cookies: document.cookie
                    .split(";")
                    .map((c) => c.trim().split("=")[0]),
            };
        });

        // Test 4: Wait and recheck (simulates page reload scenario)
        await this.addTestResult("Wait and Recheck (2s)", async () => {
            await new Promise<void>((resolve) => setTimeout(resolve, 2000));
            return await authApi.getAuthStatus();
        });

        // Test 5: Force page navigation simulation
        await this.addTestResult("Navigation Simulation", async () => {
            // Simulate what happens during navigation
            const beforeNav = await authApi.getAuthStatus();

            // Wait a bit (simulates navigation time)
            await new Promise<void>((resolve) => setTimeout(resolve, 500));

            const afterNav = await authApi.getCurrentUser();

            return {
                beforeNavigation: beforeNav,
                afterNavigation: afterNav,
                persistent:
                    beforeNav?.isAuthenticated === afterNav?.applicantId
                        ? true
                        : false,
            };
        });

        // Test 6: Cookie validation
        await this.addTestResult("Cookie Validation", async () => {
            const cookies = document.cookie.split(";").reduce(
                (acc, cookie) => {
                    const [name, value] = cookie.trim().split("=");
                    acc[name] = value;
                    return acc;
                },
                {} as Record<string, string>
            );

            const authCookies = Object.keys(cookies).filter(
                (name) =>
                    name.includes("Auth") ||
                    name.includes("Cookie") ||
                    name.includes(".AspNetCore")
            );

            return {
                totalCookies: Object.keys(cookies).length,
                authCookies,
                hasAuthCookie: authCookies.length > 0,
                cookieDetails: authCookies.reduce(
                    (acc, name) => {
                        acc[name] = cookies[name] ? "present" : "missing";
                        return acc;
                    },
                    {} as Record<string, string>
                ),
            };
        });

        // Test 7: HTTPS/Security check
        await this.addTestResult("Security Configuration Check", async () => {
            return {
                protocol: window.location.protocol,
                isSecure: window.location.protocol === "https:",
                hostname: window.location.hostname,
                port: window.location.port,
                isLocalhost: window.location.hostname === "localhost",
                crossOrigin: window.location.hostname !== "localhost",
            };
        });

        this.isRunning = false;
        return this.results;
    }

    private async addTestResult(
        testName: string,
        testFn: () => Promise<unknown>
    ): Promise<void> {
        const startTime = Date.now();
        try {
            const data = await testFn();
            const duration = Date.now() - startTime;

            this.results.push({
                timestamp: new Date().toISOString(),
                test: testName,
                success: true,
                data,
                duration,
            });
        } catch (error) {
            const duration = Date.now() - startTime;
            const errorMessage =
                error instanceof Error ? error.message : "Unknown error";

            this.results.push({
                timestamp: new Date().toISOString(),
                test: testName,
                success: false,
                error: errorMessage,
                duration,
            });
        }
    }

    getResults(): SessionTestResult[] {
        return [...this.results];
    }

    generateReport(): string {
        const total = this.results.length;
        const passed = this.results.filter((r) => r.success).length;
        const failed = total - passed;

        let report = `Authentication Persistence Test Report\n`;
        report += `=====================================\n`;
        report += `Total Tests: ${total}\n`;
        report += `Passed: ${passed}\n`;
        report += `Failed: ${failed}\n`;
        report += `Success Rate: ${Math.round((passed / total) * 100)}%\n\n`;

        this.results.forEach((result, index) => {
            report += `${index + 1}. ${result.test}\n`;
            report += `   Status: ${result.success ? "? PASS" : "? FAIL"}\n`;
            report += `   Duration: ${result.duration}ms\n`;

            if (result.error) {
                report += `   Error: ${result.error}\n`;
            }

            if (result.data && typeof result.data === "object") {
                report += `   Data: ${JSON.stringify(result.data, null, 2)}\n`;
            }

            report += "\n";
        });

        return report;
    }

    async quickAuthCheck(): Promise<{
        isAuthenticated: boolean;
        hasSession: boolean;
        error?: string;
    }> {
        try {
            const [status, user] = await Promise.all([
                authApi.getAuthStatus().catch(() => null),
                authApi.getCurrentUser().catch(() => null),
            ]);

            const isAuthenticated = Boolean(
                status?.isAuthenticated || user?.applicantId
            );
            const hasSession = Boolean(
                status?.isAuthenticated && user?.applicantId
            );

            return {
                isAuthenticated,
                hasSession,
            };
        } catch (error) {
            return {
                isAuthenticated: false,
                hasSession: false,
                error: error instanceof Error ? error.message : "Unknown error",
            };
        }
    }
}

// Create singleton instance
export const sessionTest = new SessionPersistenceTest();

// Export utility functions
export const runAuthTest = () => sessionTest.runComprehensiveTest();
export const quickAuthCheck = () => sessionTest.quickAuthCheck();
export const getTestResults = () => sessionTest.getResults();
export const generateTestReport = () => sessionTest.generateReport();

// Add to global scope for console access
if (typeof window !== "undefined") {
    (window as unknown as Record<string, unknown>).authTest = {
        run: runAuthTest,
        quick: quickAuthCheck,
        results: getTestResults,
        report: generateTestReport,
    };
}

export default SessionPersistenceTest;
